<template>
  <div>
    <b-card v-if="loadProjectTasks">
      <center>
        <b-spinner
          variant="primary"
          label="Spinning"
        />
      </center>
    </b-card>
    <b-card v-else>
      <div class="d-flex justify-content-between align-items-center header-section">
        <b-card-title class="header-title">
          <h4>المهام</h4>
        </b-card-title>
        <div class="header-actions">
          <b-button
            v-b-tooltip.hover.top="'تحديث البيانات'"
            class="mr-1"
            variant="outline-primary"
            @click="init"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              class="mr-1"
            />
            <span class="btn-text">تحديث</span>
          </b-button>
          <b-button
            variant="primary"
            @click="showAddModal"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
              class="mr-1"
            />
            <span class="btn-text">إضافة مهمة</span>
          </b-button>
        </div>
      </div>

      <!-- Desktop Table View -->
      <div class="desktop-table-view">
        <b-table
          :items="projectTasks"
          :fields="fields"
          striped
          hover
          responsive
          :busy="loadProjectTasks"
        >
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle" />
            </div>
          </template>

          <template #cell(name)="row">
            <strong>{{ row.item.name }}</strong>
          </template>

          <template #cell(assigned_employee)="row">
            <b-dropdown
              :id="`task-assigned-dd-${row.item.id}`"
              variant="link"
              toggle-class="p-0"
              menu-class="task-dd-menu"
              no-caret
              right
              dropup
              boundary="window"
              :disabled="assignedUpdatingTaskId === row.item.id"
              @show="onAssignedDropdownShow"
            >
              <template #button-content>
                <span
                  class="assigned-inline-display d-inline-flex align-items-center"
                  role="button"
                  tabindex="0"
                >
                  <span
                    v-if="row.item.assigned_employee"
                    class="font-weight-bold"
                  >
                    {{ row.item.assigned_employee.full_name }}
                  </span>
                  <span
                    v-else
                    class="text-muted"
                  >-</span>
                  <b-spinner
                    v-if="assignedUpdatingTaskId === row.item.id"
                    small
                    class="ml-50"
                  />
                </span>
              </template>

              <b-dropdown-form @submit.stop.prevent>
                <b-form-input
                  v-model="assignedSearch"
                  size="sm"
                  placeholder="بحث عن موظف..."
                  @input="onAssignedSearchInput"
                />
              </b-dropdown-form>
              <b-dropdown-divider />

              <b-dropdown-item-button
                v-for="emp in filteredEmployeesOptions"
                :key="emp.id"
                :disabled="assignedUpdatingTaskId === row.item.id || emp.id === (row.item.assigned_to || (row.item.assigned_employee && row.item.assigned_employee.id))"
                @click="updateTaskAssignedInline(row.item, emp.id)"
              >
                {{ emp.full_name }}
              </b-dropdown-item-button>

              <b-dropdown-text
                v-if="filteredEmployeesOptions.length === 0"
                class="text-muted"
              >
                لا توجد نتائج
              </b-dropdown-text>
            </b-dropdown>
          </template>

          <template #cell(status)="row">
            <b-dropdown
              :id="`task-status-dd-${row.item.id}`"
              variant="link"
              toggle-class="p-0"
              menu-class="py-0 task-dd-menu"
              no-caret
              right
              dropup
              boundary="window"
              :disabled="statusUpdatingTaskId === row.item.id"
            >
              <template #button-content>
                <b-badge
                  class="cursor-pointer"
                  :variant="getStatusVariant(row.item.status)"
                >
                  <b-spinner
                    v-if="statusUpdatingTaskId === row.item.id"
                    small
                    class="mr-25"
                  />
                  {{ getStatusText(row.item.status) }}
                </b-badge>
              </template>

              <b-dropdown-item-button
                v-for="opt in statusOptions"
                :key="opt.value"
                :disabled="statusUpdatingTaskId === row.item.id || opt.value === row.item.status"
                @click="updateTaskStatusInline(row.item, opt.value)"
              >
                <span>{{ opt.text }}</span>
                <span
                  v-if="opt.value === row.item.status"
                  class="ml-50 text-muted"
                >(الحالية)</span>
              </b-dropdown-item-button>
            </b-dropdown>
          </template>

          <template #cell(priority)="row">
            <b-badge :variant="getPriorityVariant(row.item.priority)">
              {{ row.item.priority_label || row.item.priority }}
            </b-badge>
          </template>

          <template #cell(estimated_hours)="row">
            {{ row.item.estimated_hours || '-' }}
          </template>

          <template #cell(actual_hours)="row">
            {{ row.item.actual_hours || '-' }}
          </template>

          <template #cell(due_date)="row">
            {{ row.item.due_date ? formatDate(row.item.due_date) : '-' }}
          </template>

          <template #cell(actions)="row">
            <div class="d-flex gap-2 actions-cell">
              <!-- <b-button
              variant="outline-primary"
              size="sm"
              class="action-btn"
              @click="showStatusModal(row.item)"
              v-b-tooltip.hover.top="'تغيير الحالة'"
            >
              <feather-icon icon="EditIcon" size="14" />
            </b-button> -->
              <!-- <b-button
              variant="outline-info"
              size="sm"
              class="action-btn"
              @click="showEditModal(row.item)"
              v-b-tooltip.hover.top="'تعديل'"
            >
              <feather-icon icon="Edit2Icon" size="14" />
            </b-button> -->
              <b-button
                v-b-tooltip.hover.top="'عرض'"
                variant="outline-primary"
                size="sm"
                @click="showTask(row.item)"
              >
                <feather-icon
                  icon="EyeIcon"
                  size="14"
                />
              </b-button>
              <b-button
                v-if="hasRole('admin')"
                v-b-tooltip.hover.top="'حذف'"
                variant="outline-danger"
                size="sm"
                class="action-btn"
                @click="confirmDelete(row.item)"
              >
                <feather-icon
                  icon="TrashIcon"
                  size="14"
                />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>

      <!-- Mobile Card View -->
      <div class="mobile-card-view">
        <div
          v-if="loadProjectTasks"
          class="text-center my-4"
        >
          <b-spinner variant="primary" />
        </div>
        <div
          v-else-if="!projectTasks || projectTasks.length === 0"
          class="text-center my-4 text-muted"
        >
          لا توجد مهام
        </div>
        <div v-else>
          <b-card
            v-for="task in projectTasks"
            :key="task.id"
            class="task-card mb-3"
          >
            <!-- Task Name -->
            <div class="task-card-header">
              <h5 class="task-name">
                {{ task.name }}
              </h5>
            </div>

            <!-- Task Details -->
            <div class="task-card-body">
              <!-- Assigned Employee -->
              <div class="task-detail-row">
                <span class="task-label">المكلف:</span>
                <b-dropdown
                  :id="`mobile-task-assigned-dd-${task.id}`"
                  variant="link"
                  toggle-class="p-0 task-dropdown-link"
                  menu-class="task-dd-menu"
                  no-caret
                  right
                  boundary="window"
                  :disabled="assignedUpdatingTaskId === task.id"
                  @show="onAssignedDropdownShow"
                >
                  <template #button-content>
                    <span class="task-value d-inline-flex align-items-center">
                      <span
                        v-if="task.assigned_employee"
                        class="font-weight-bold"
                      >
                        {{ task.assigned_employee.full_name }}
                      </span>
                      <span
                        v-else
                        class="text-muted"
                      >-</span>
                      <b-spinner
                        v-if="assignedUpdatingTaskId === task.id"
                        small
                        class="ml-1"
                      />
                    </span>
                  </template>
                  <b-dropdown-form @submit.stop.prevent>
                    <b-form-input
                      v-model="assignedSearch"
                      size="sm"
                      placeholder="بحث عن موظف..."
                      @input="onAssignedSearchInput"
                    />
                  </b-dropdown-form>
                  <b-dropdown-divider />
                  <b-dropdown-item-button
                    v-for="emp in filteredEmployeesOptions"
                    :key="emp.id"
                    :disabled="assignedUpdatingTaskId === task.id || emp.id === (task.assigned_to || (task.assigned_employee && task.assigned_employee.id))"
                    @click="updateTaskAssignedInline(task, emp.id)"
                  >
                    {{ emp.full_name }}
                  </b-dropdown-item-button>
                  <b-dropdown-text
                    v-if="filteredEmployeesOptions.length === 0"
                    class="text-muted"
                  >
                    لا توجد نتائج
                  </b-dropdown-text>
                </b-dropdown>
              </div>

              <!-- Status -->
              <div class="task-detail-row">
                <span class="task-label">الحالة:</span>
                <b-dropdown
                  :id="`mobile-task-status-dd-${task.id}`"
                  variant="link"
                  toggle-class="p-0 task-dropdown-link"
                  menu-class="py-0 task-dd-menu"
                  no-caret
                  right
                  boundary="window"
                  :disabled="statusUpdatingTaskId === task.id"
                >
                  <template #button-content>
                    <b-badge
                      class="cursor-pointer"
                      :variant="getStatusVariant(task.status)"
                    >
                      <b-spinner
                        v-if="statusUpdatingTaskId === task.id"
                        small
                        class="mr-1"
                      />
                      {{ task.status_label || task.status }}
                    </b-badge>
                  </template>
                  <b-dropdown-item-button
                    v-for="opt in statusOptions"
                    :key="opt.value"
                    :disabled="statusUpdatingTaskId === task.id || opt.value === task.status"
                    @click="updateTaskStatusInline(task, opt.value)"
                  >
                    <span>{{ opt.text }}</span>
                    <span
                      v-if="opt.value === task.status"
                      class="ml-1 text-muted"
                    >(الحالية)</span>
                  </b-dropdown-item-button>
                </b-dropdown>
              </div>

              <!-- Priority -->
              <div class="task-detail-row">
                <span class="task-label">الأولوية:</span>
                <b-badge
                  :variant="getPriorityVariant(task.priority)"
                  class="task-value"
                >
                  {{ task.priority_label || task.priority }}
                </b-badge>
              </div>

              <!-- Due Date -->
              <div class="task-detail-row">
                <span class="task-label">تاريخ الاستحقاق:</span>
                <span class="task-value">{{ task.due_date ? formatDate(task.due_date) : '-' }}</span>
              </div>

              <!-- Actions -->
              <div class="task-actions-row">
                <!-- <b-button
                  variant="outline-primary"
                  size="sm"
                  class="action-btn-mobile"
                  @click="showStatusModal(task)"
                >
                  <feather-icon icon="EditIcon" size="14" class="mr-1" />
                  حالة
                </b-button> -->
                <!-- <b-button
                  variant="outline-info"
                  size="sm"
                  class="action-btn-mobile"
                  @click="showEditModal(task)"
                >
                  <feather-icon icon="Edit2Icon" size="14" class="mr-1" />
                  تعديل
                </b-button> -->

                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="action-btn-mobile"
                  @click="showTask(task)"
                >
                  <feather-icon
                    icon="EyeIcon"
                    size="14"
                    class="mr-1"
                  />
                  عرض
                </b-button>
                <b-button
                  v-if="hasRole('admin')"
                  variant="outline-danger"
                  size="sm"
                  class="action-btn-mobile"
                  @click="confirmDelete(task)"
                >
                  <feather-icon
                    icon="TrashIcon"
                    size="14"
                    class="mr-1"
                  />
                  حذف
                </b-button>
              </div>
            </div>
          </b-card>
        </div>
      </div>

      <!-- Pagination -->
      <div
        v-if="projectTasksMeta"
        class="mt-3"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="projectTasksMeta.total || 0"
          :per-page="projectTasksMeta.per_page || 10"
          align="center"
          @change="handlePageChange"
        />
        <div class="text-center mt-2 text-muted">
          <span v-if="projectTasksMeta.from && projectTasksMeta.to && projectTasksMeta.total">
            عرض {{ projectTasksMeta.from }} إلى {{ projectTasksMeta.to }} من {{ projectTasksMeta.total }} مهمة
          </span>
          <span v-else-if="projectTasksMeta.total">
            إجمالي {{ projectTasksMeta.total }} مهمة
          </span>
        </div>
      </div>

      <!-- Add/Edit Task Modal -->
      <b-modal
        :id="taskModalId"
        :title="isEditMode ? 'تعديل المهمة' : 'إضافة مهمة'"
        size="lg"
        class="task-modal"
      >
        <validation-observer ref="form">
          <b-form @submit.prevent>
            <b-row class="form-row">
              <!-- Task Name -->
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="name"
                  rules="required"
                >
                  <b-form-group
                    label="اسم المهمة"
                    label-for="task-name"
                  >
                    <b-form-input
                      id="task-name"
                      v-model="form.name"
                      placeholder="أدخل اسم المهمة"
                      required
                    />
                    <small
                      v-if="errors[0]"
                      class="text-danger"
                    >{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Assigned Employee -->
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="assigned_employee"
                  rules="required"
                >
                  <b-form-group
                    label="المكلف"
                    label-for="task-assigned-employee"
                  >
                    <v-select
                      v-model="form.assigned_to"
                      :options="employees.data || []"
                      :reduce="val => val.id"
                      :get-option-label="option => option.full_name"
                      :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                      placeholder="اختر الموظف"
                      @search="callEmployees"
                    >
                      <template #option="{ full_name }">
                        <span>{{ full_name }}</span>
                      </template>
                      <template #selected-option="{ full_name }">
                        <span>{{ full_name }}</span>
                      </template>
                    </v-select>
                    <small
                      v-if="errors[0]"
                      class="text-danger"
                    >{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Description -->
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="description"
                >
                  <b-form-group
                    label="الوصف"
                    label-for="task-description"
                  >
                    <b-form-textarea
                      id="task-description"
                      v-model="form.description"
                      placeholder="أدخل وصف المهمة"
                      rows="3"
                    />
                    <small
                      v-if="errors[0]"
                      class="text-danger"
                    >{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Priority -->
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="priority"
                  rules="required"
                >
                  <b-form-group
                    label="الأولوية"
                    label-for="task-priority"
                  >
                    <b-form-select
                      id="task-priority"
                      v-model="form.priority"
                      :options="priorityOptions"
                    />
                    <small
                      v-if="errors[0]"
                      class="text-danger"
                    >{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Due Date -->
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="due_date"
                >
                  <b-form-group
                    label="تاريخ الاستحقاق"
                    label-for="task-due-date"
                  >
                    <b-form-input
                      id="task-due-date"
                      v-model="form.due_date"
                      type="date"
                    />
                    <small
                      v-if="errors[0]"
                      class="text-danger"
                    >{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Notes -->
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="notes"
                >
                  <b-form-group
                    label="ملاحظات"
                    label-for="task-notes"
                  >
                    <b-form-textarea
                      id="task-notes"
                      v-model="form.notes"
                      placeholder="أدخل ملاحظات"
                      rows="2"
                    />
                    <small
                      v-if="errors[0]"
                      class="text-danger"
                    >{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>

        <template #modal-footer="{ cancel }">
          <div class="modal-footer-buttons">
            <b-button
              variant="secondary"
              class="footer-btn"
              @click="cancel"
            >
              إلغاء
            </b-button>
            <b-button
              variant="primary"
              type="submit"
              class="footer-btn"
              @click="handleSubmit"
            >
              {{ isEditMode ? 'تحديث' : 'إضافة' }}
            </b-button>
          </div>
        </template>
      </b-modal>

      <!-- Status Update Modal -->
      <b-modal
        id="status-modal"
        title="تغيير حالة المهمة"
        @ok="handleStatusUpdate"
      >
        <b-form-group
          label="الحالة"
          label-for="task-status"
        >
          <b-form-select
            id="task-status"
            v-model="selectedStatus"
            :options="statusOptions"
          />
        </b-form-group>

        <template #modal-footer="{ ok, cancel }">
          <div class="modal-footer-buttons">
            <b-button
              variant="secondary"
              class="footer-btn"
              @click="cancel"
            >
              إلغاء
            </b-button>
            <b-button
              variant="primary"
              class="footer-btn"
              @click="ok"
            >
              تحديث
            </b-button>
          </div>
        </template>
      </b-modal>
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  BCard,
  BCardTitle,
  BSpinner,
  BTable,
  BButton,
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormSelect,
  BDropdown,
  BDropdownForm,
  BDropdownItemButton,
  BDropdownDivider,
  BDropdownText,
  BRow,
  BCol,
  BBadge,
  BPagination,
  BAvatar,
  VBTooltip,
  BFormRadioGroup,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver } from 'vee-validate'

export default {
  name: 'ProjectTasksComponent',
  components: {
    BCard,
    BCardTitle,
    BSpinner,
    BTable,
    BButton,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BDropdown,
    BDropdownForm,
    BDropdownItemButton,
    BDropdownDivider,
    BDropdownText,
    BRow,
    BCol,
    BBadge,
    BPagination,
    BAvatar,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    BFormRadioGroup,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      taskModalId: 'task-modal',
      isEditMode: false,
      selectedTask: null,
      selectedStatus: null,
      statusUpdatingTaskId: null,
      assignedUpdatingTaskId: null,
      assignedSearch: '',
      form: {
        assigned_to: null,
        name: '',
        description: '',
        priority: 'medium',
        due_date: (() => {
          const today = new Date()
          const year = today.getFullYear()
          const month = String(today.getMonth() + 1).padStart(2, '0')
          const day = String(today.getDate()).padStart(2, '0')
          return `${year}-${month}-${day}`
        })(),
        estimated_hours: null,
        actual_hours: null,
        notes: '',
      },
      fields: [
        { key: 'name', label: 'اسم المهمة', sortable: true },
        { key: 'assigned_employee', label: 'المكلف', sortable: false },
        { key: 'status', label: 'الحالة', sortable: true },
        { key: 'priority', label: 'الأولوية', sortable: true },
        // { key: 'estimated_hours', label: 'الساعات المتوقعة' },
        // { key: 'actual_hours', label: 'الساعات الفعلية' },
        { key: 'due_date', label: 'تاريخ الاستحقاق', sortable: true },
        { key: 'actions', label: 'الإجراءات' },
      ],
      currentPage: 1,
      statusOptions: [
        { value: 'pending', text: 'قيد الانتظار' },
        { value: 'in_progress', text: 'جار' },
        { value: 'completed', text: 'تم' },
        { value: 'blocked', text: 'تعذر' },
        // { value: 'review', text: 'قيد المراجعة' },
        // { value: 'client_pending', text: 'في انتظار العميل' },
      ],
      priorityOptions: [
        { value: 'low', text: 'منخفضة' },
        { value: 'medium', text: 'متوسطة' },
        { value: 'high', text: 'عالية' },
        { value: 'urgent', text: 'عاجلة' },
      ],
    }
  },
  computed: {
    ...mapGetters({
      projectTasks: 'projects/projectTasks',
      loadProjectTasks: 'projects/loadProjectTasks',
      projectTasksMeta: 'projects/projectTasksMeta',
      employees: 'employees/employees',
    }),
    id() {
      return this.$route.params.id
    },
    employeesOptions() {
      // employees getter sometimes holds array or an object with { data: [] }
      const e = this.employees
      if (Array.isArray(e)) return e
      if (e && Array.isArray(e.data)) return e.data
      if (e && e.data && Array.isArray(e.data.data)) return e.data.data
      return []
    },
    filteredEmployeesOptions() {
      const q = (this.assignedSearch || '').trim().toLowerCase()
      const list = this.employeesOptions || []
      if (!q) return list
      return list.filter(emp => ((emp && emp.full_name) || '').toLowerCase().includes(q))
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    showTask(task) {
      this.$router.push({ name: 'taskDetails', params: { id: task.id } })
    },
    init() {
      this.$store.dispatch('projects/getProjectTasks', {
        id: this.id,
        page: this.currentPage,
        per_page: 10,
      })
      this.$store.dispatch('employees/listEmployees', { query: {} })
    },
    callEmployees(search) {
      if (search) {
        const query = search ? { search } : {}
        this.$store.dispatch('employees/listEmployees', { query }).then(res => {
        })
      }
    },
    handlePageChange(page) {
      this.currentPage = page
      this.$store.dispatch('projects/getProjectTasks', {
        id: this.id,
        page: this.currentPage,
        per_page: 10,
      })
    },
    getInitials(name) {
      if (!name) return '?'
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },
    showAddModal() {
      this.isEditMode = false
      this.resetForm()
      this.$bvModal.show(this.taskModalId)
    },
    showEditModal(task) {
      this.isEditMode = true
      this.selectedTask = task
      this.form = {
        assigned_to: task.assigned_to || null,
        name: task.name || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        due_date: task.due_date || '',
        estimated_hours: task.estimated_hours || null,
        actual_hours: task.actual_hours || null,
        notes: task.notes || '',
      }
      // if task.assigned_employee.id is not in the employees.data, then add it
      if (!this.employees.data.some(employee => employee.id === task.assigned_employee.id)) {
        this.employees.data.push({ id: task.assigned_employee.id, full_name: task.assigned_employee.full_name })
      }
      this.$bvModal.show(this.taskModalId)
    },
    showStatusModal(task) {
      this.selectedTask = task
      this.selectedStatus = task.status
      this.$bvModal.show('status-modal')
    },
    handleSubmit() {
      this.$refs.form.validate().then(success => {
        if (success) {
          this.submitForm()
        }
      })
    },
    submitForm() {
      const data = { ...this.form }

      if (this.isEditMode) {
        this.$store
          .dispatch('projects/updateProjectTask', {
            projectId: this.id,
            taskId: this.selectedTask.id,
            data,
          })
          .then(() => {
            this.$toast.success('تم تحديث المهمة بنجاح')
            this.$bvModal.hide(this.taskModalId)
            this.init()
          })
          .catch(error => {
            this.$toast.error('حدث خطأ أثناء تحديث المهمة')
            console.error(error)
          })
      } else {
        this.$store
          .dispatch('projects/createProjectTask', {
            projectId: this.id,
            data,
          })
          .then(() => {
            this.$toast.success('تم إضافة المهمة بنجاح')
            this.$bvModal.hide(this.taskModalId)
            this.init()
          })
          .catch(error => {
            this.$toast.error('حدث خطأ أثناء إضافة المهمة')
            console.error(error)
          })
      }
    },
    handleStatusUpdate() {
      if (!this.selectedStatus) {
        this.$toast.error('يرجى اختيار حالة')
        return
      }

      this.$store
        .dispatch('projects/updateProjectTaskStatus', {
          projectId: this.id,
          taskId: this.selectedTask.id,
          status: this.selectedStatus,
        })
        .then(() => {
          this.$toast.success('تم تحديث حالة المهمة بنجاح')
          this.$bvModal.hide('status-modal')
          this.init()
        })
        .catch(error => {
          this.$toast.error('حدث خطأ أثناء تحديث حالة المهمة')
          console.error(error)
        })
    },
    onAssignedDropdownShow() {
      this.assignedSearch = ''
      // Make sure we have initial employees list
      this.callEmployees('')
    },
    onAssignedSearchInput() {
      // Remote search with debounce via the existing onEmployeeSearch/callEmployees pattern
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.callEmployees(this.assignedSearch)
      }, 300)
    },
    updateTaskAssignedInline(task, assignedTo) {
      if (!task || !task.id) return
      if (!assignedTo) return
      if (this.assignedUpdatingTaskId === task.id) return

      this.assignedUpdatingTaskId = task.id
      this.$store
        .dispatch('projects/updateProjectAssignee', {
          projectId: this.id,
          taskId: task.id,
          data: { assigned_to: assignedTo },
        })
        .then(() => {
          this.$toast.success('تم تحديث المكلف بنجاح')
          this.init()
        })
        .catch(error => {
          this.$toast.error('حدث خطأ أثناء تحديث المكلف')
          console.error(error)
        })
        .finally(() => {
          if (this.assignedUpdatingTaskId === task.id) this.assignedUpdatingTaskId = null
        })
    },
    updateTaskStatusInline(task, status) {
      if (!task || !task.id) return
      if (!status || status === task.status) return

      this.statusUpdatingTaskId = task.id

      this.$store
        .dispatch('projects/updateProjectTaskStatus', {
          projectId: this.id,
          taskId: task.id,
          status,
        })
        .then(() => {
          this.$toast.success('تم تحديث حالة المهمة بنجاح')
          this.init()
        })
        .catch(error => {
          this.$toast.error('حدث خطأ أثناء تحديث حالة المهمة')
          console.error(error)
        })
        .finally(() => {
          if (this.statusUpdatingTaskId === task.id) this.statusUpdatingTaskId = null
        })
    },
    confirmDelete(task) {
      this.$swal({
        title: 'هل أنت متأكد؟',
        text: 'سيتم حذف هذه المهمة بشكل نهائي',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'نعم، احذف',
        cancelButtonText: 'إلغاء',
        confirmButtonColor: '#d33',
      }).then(result => {
        if (result.isConfirmed) {
          this.$store
            .dispatch('projects/deleteProjectTask', {
              projectId: this.id,
              taskId: task.id,
            })
            .then(() => {
              this.$toast.success('تم حذف المهمة بنجاح')
              this.init()
            })
            .catch(error => {
              this.$toast.error('حدث خطأ أثناء حذف المهمة')
              console.error(error)
            })
        }
      })
    },
    resetForm() {
      this.form = {
        name: '',
        assigned_to: null,
        description: '',
        priority: 'medium',
        due_date: this.getCurrentDate(),
        estimated_hours: null,
        actual_hours: null,
        notes: '',
      }
      this.selectedTask = null
      if (this.$refs.form) {
        this.$refs.form.reset()
      }
    },
    getCurrentDate() {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        in_progress: 'info',
        completed: 'success',
        review: 'primary',
        blocked: 'danger',
        client_pending: 'secondary',
      }
      return variants[status] || 'secondary'
    },
    getStatusText(status) {
      const option = this.statusOptions.find(opt => opt.value === status)
      return option ? option.text : status
    },
    onEmployeeSearch(search, loading) {
      // Clear previous timeout
      clearTimeout(this.searchTimeout)

      // If search is empty or has content, make the call
      if (loading) {
        loading(true)
      }

      // Debounce the API call to avoid too many requests
      this.searchTimeout = setTimeout(() => {
        this.callEmployees(search)
        if (loading && typeof loading === 'function') {
          loading(false)
        }
      }, 300) // 300ms delay
    },
    getPriorityVariant(priority) {
      const variants = {
        low: 'secondary',
        medium: 'info',
        high: 'warning',
        urgent: 'danger',
      }
      return variants[priority] || 'secondary'
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('ar-EG')
    },
    hasRole(role) {
      try {
        const userRoles = this.$store.getters['users/user_roles']
        if (!userRoles) return false
        return Object.values(userRoles).includes(role)
      } catch (e) {
        return false
      }
    },
  },
}
</script>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}
.cursor-pointer {
  cursor: pointer;
}
.task-inline-editor {
  min-width: 220px;
}
.assigned-inline-display {
  cursor: pointer;
}

/* Keep dropdown menus compact + scrollable */
.task-dd-menu {
  max-height: 160px;
  overflow-y: auto;
  min-width: 240px;
}

/* Desktop and Mobile View Toggle */
.desktop-table-view {
  display: block;
}

.mobile-card-view {
  display: none;
}

/* Mobile Card Styles */
.task-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.task-card-header {
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
}

.task-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  word-break: break-word;
}

.task-card-body {
  padding: 0;
}

.task-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f5f5f5;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.task-detail-row:last-of-type {
  border-bottom: none;
}

.task-label {
  font-weight: 600;
  color: #666;
  font-size: 0.9rem;
  min-width: 100px;
}

.task-value {
  color: #333;
  font-size: 0.9rem;
  text-align: right;
  flex: 1;
}

.task-dropdown-link {
  text-decoration: none;
  color: inherit;
}

.task-dropdown-link:hover {
  text-decoration: none;
}

.task-actions-row {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
  flex-wrap: wrap;
}

.action-btn-mobile {
  flex: 1;
  min-width: 100px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  /* Hide desktop table, show mobile cards */
  .desktop-table-view {
    display: none;
  }

  .mobile-card-view {
    display: block;
  }

  .header-section {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 1rem;
  }

  .header-title {
    width: 100%;
    margin-bottom: 0;
  }

  .header-title h4 {
    font-size: 1.25rem;
    margin-bottom: 0;
  }

  .header-actions {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .header-actions .btn {
    width: 100%;
    justify-content: center;
    min-height: 44px; /* Touch-friendly */
  }

  .btn-text {
    display: inline;
  }

  .task-card {
    margin-bottom: 1rem;
  }

  .task-name {
    font-size: 1rem;
  }

  .task-detail-row {
    flex-direction: column;
    align-items: flex-start;
    padding: 0.5rem 0;
  }

  .task-label {
    min-width: auto;
    margin-bottom: 0.25rem;
  }

  .task-value {
    width: 100%;
    text-align: right;
  }

  .task-actions-row {
    flex-direction: column;
  }

  .action-btn-mobile {
    width: 100%;
    min-width: auto;
  }

  /* Modal improvements */
  .task-modal ::v-deep .modal-dialog {
    margin: 0.5rem;
    max-width: calc(100% - 1rem);
  }

  .task-modal ::v-deep .modal-content {
    border-radius: 0.5rem;
  }

  .form-row {
    margin: 0;
  }

  .form-row .col-12 {
    padding-left: 0;
    padding-right: 0;
    margin-bottom: 1rem;
  }

  .modal-footer-buttons {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .footer-btn {
    width: 100%;
    min-height: 44px;
  }

  /* Pagination */
  ::v-deep .pagination {
    flex-wrap: wrap;
    justify-content: center;
  }

  ::v-deep .page-link {
    min-width: 44px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
  }

  /* Dropdown improvements */
  .task-dd-menu {
    min-width: 200px;
    max-width: 90vw;
  }

  /* Badge improvements */
  ::v-deep .badge {
    font-size: 0.75rem;
    padding: 0.35em 0.65em;
  }
}

@media (max-width: 576px) {
  .header-title h4 {
    font-size: 1.1rem;
  }

  .header-actions .btn {
    font-size: 0.9rem;
    padding: 0.5rem 0.75rem;
  }

  .action-btn {
    min-width: 40px;
    min-height: 40px;
    padding: 0.375rem;
  }

  .task-modal ::v-deep .modal-dialog {
    margin: 0.25rem;
    max-width: calc(100% - 0.5rem);
  }

  .task-modal ::v-deep .modal-header,
  .task-modal ::v-deep .modal-body,
  .task-modal ::v-deep .modal-footer {
    padding: 0.75rem;
  }

  .task-name {
    font-size: 0.95rem;
  }

  .task-label {
    font-size: 0.85rem;
  }

  .task-value {
    font-size: 0.85rem;
  }

  .action-btn-mobile {
    font-size: 0.8rem;
    padding: 0.5rem;
  }
}

@media (max-width: 360px) {
  .header-title h4 {
    font-size: 1rem;
  }

  .header-actions .btn {
    font-size: 0.85rem;
    padding: 0.4rem 0.6rem;
  }

  .action-btn {
    min-width: 36px;
    min-height: 36px;
    padding: 0.25rem;
  }

  ::v-deep .table {
    font-size: 0.8rem;
  }

  ::v-deep .table th,
  ::v-deep .table td {
    padding: 0.375rem 0.2rem;
  }
}

/* Touch-friendly improvements */
@media (hover: none) and (pointer: coarse) {
  .action-btn {
    min-width: 44px;
    min-height: 44px;
  }

  .header-actions .btn {
    min-height: 44px;
  }

  .footer-btn {
    min-height: 44px;
  }

  ::v-deep .page-link {
    min-width: 44px;
    min-height: 44px;
  }
}
</style>
